import React, { useMemo } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { formatNull, formatDate } from '../../Utils/GlobalUtils';

import WarningPanel from '../WarningPanel';
import Loading from '../Loading';

import styles from '../../styles/DynamicModal.module.css';
import miniTable from '../../styles/MiniTables.module.css';

import IconContainer from '../../Components/IconContainer';
import { ReactComponent as XIcon } from '../../icons/x-close.svg';

export default function FilterProposedCases({ field=null, filters, setFilters, DEFAULT_FILTERS, closeModal }) {
    const [entries, setEntries] = React.useState([]);
    const [reload, setReload] = React.useState(true);
    const [warning, setWarning] = React.useState('');
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        const token = localStorage.getItem('token');

        axios.get(`/api/get-one-field-entries-in-proposed-cases?field=${field}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        })
        .then(response => {
            setEntries(response.data);
        })
        .catch(error => {
            console.error('Ошибка при получении данных:', error);
            setWarning(`${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`);
        }).finally(() => {
            setLoading(false);
            setReload(false);
        })
    }, [reload]);

    const [searchNumber, setSearchNumber] = React.useState('');

    const filteredNumbers = useMemo(() => {
        const query = searchNumber.trim().toLowerCase();
        if (!query) return entries;

        return entries.filter(entry => {
            const fullEntry = `${entry.number}`.toLowerCase();
            return fullEntry.includes(query) || entry.number.toLowerCase().includes(query);
        });
    }, [entries, searchNumber]);

    const [searchDebtor, setSearchDebtor] = React.useState('');

    const filteredDebtors = useMemo(() => {
        const query = searchDebtor.trim().toLowerCase();
        if (!query) return entries;

        return entries.filter(entry => {
            const fullEntry = `${entry.name}`.toLowerCase();
            return fullEntry.includes(query) || entry.name.toLowerCase().includes(query);
        });
    }, [entries, searchDebtor]);

    const [searchCountPretenders, setSearchCountPretenders] = React.useState('');

    const filteredCountPretenders = useMemo(() => {
        const query = searchCountPretenders.trim();
        if (!query) return entries;

        return entries.filter(entry => {
            const fullEntry = `${entry.applications_count}`;
            return fullEntry.includes(query);
        });
    }, [entries, searchCountPretenders]);



    const allSelectedNumbers =
        filteredNumbers.length > 0 &&
        filteredNumbers.every(entry => filters.numbers.includes(entry.number));

    const handleSelectAllNumbers = () => {
        setFilters(prev => ({
            ...prev,
            numbers: allSelectedNumbers
                ? prev.numbers.filter(
                    number => !filteredNumbers.some(entry => entry.number === number)
                )
                : [
                    ...new Set([
                        ...prev.numbers,
                        ...filteredNumbers.map(entry => entry.number)
                    ])
                ]
        }));
    };

    const allSelectedDebtors =
        filteredDebtors.length > 0 &&
        filteredDebtors.every(entry => filters.debtors.includes(entry.id));

    const handleSelectAllDebtors = () => {
        setFilters(prev => ({
            ...prev,
            debtors: allSelectedDebtors
                ? prev.debtors.filter(
                    id => !filteredDebtors.some(entry => entry.id === id)
                )
                : [
                    ...new Set([
                        ...prev.debtors,
                        ...filteredDebtors.map(entry => entry.id)
                    ])
                ]
        }));
    };

    const allSelectedCounts =
        filteredCountPretenders.length > 0 &&
        filteredCountPretenders.every(entry => filters.count_pretenders.includes(entry.applications_count));

    const handleSelectAllCounts = () => {
        setFilters(prev => ({
            ...prev,
            count_pretenders: allSelectedCounts
                ? prev.count_pretenders.filter(
                    count_pretenders => !filteredCountPretenders.some(entry => entry.applications_count === count_pretenders)
                )
                : [
                    ...new Set([
                        ...prev.count_pretenders,
                        ...filteredCountPretenders.map(entry => entry.applications_count)
                    ])
                ]
        }));
    };

    const [dateMailing, setDateMailing] = React.useState([]);

    React.useEffect(() => {
        if (dateMailing.length > 0) {
            const sorted = [...dateMailing].sort((a, b) => new Date(a) - new Date(b));
            if (field=='date_mailing') {
                setFilters(f => ({
                    ...f,
                    date_mailing_from: sorted[0],
                    date_mailing_to: sorted[sorted.length - 1],
                }));
            } else if (field=='date_consent_deadline') {
                setFilters(f => ({
                    ...f,
                    date_consent_deadline_from: sorted[0],
                    date_consent_deadline_to: sorted[sorted.length - 1],
                }));
            }
        } 
    }, [dateMailing]);

    const [selectedMonths, setSelectedMonths] = React.useState([]);

    const monthNames = [
        'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
        'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
    ];

    const formatLocal = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    const now = new Date();

    const months = Array.from({ length: 12 }, (_, i) => {
        // i=0 - текущий месяц, дальше в прошлое
        const targetDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const year = targetDate.getFullYear();
        const monthIndex = targetDate.getMonth();

        const from = new Date(year, monthIndex, 1);
        const to = new Date(year, monthIndex + 1, 0);

        return {
            key: `${year}-${String(monthIndex + 1).padStart(2, '0')}`, // "2026-07"
            label: `${monthNames[monthIndex]} ${year}`,
            from: formatLocal(from),
            to: formatLocal(to),
        };
    });

    React.useEffect(() => {
        if (filters.date_mailing_to < filters.date_mailing_from) {
            setFilters((prev) => ({
                ...prev,
                date_mailing_to: filters.date_mailing_from,
            }));
        }
    }, [filters.date_mailing_to]);

    React.useEffect(() => {
        if (filters.date_consent_deadline_to < filters.date_consent_deadline_from) {
            setFilters((prev) => ({
                ...prev,
                date_consent_deadline_to: filters.date_consent_deadline_from,
            }));
        }
    }, [filters.date_consent_deadline_to]);


    const handleChange = (field) => (e) => {
        const value = e.target.value;

        setFilters((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const addFilter = () => {
        closeModal();
    };

    const resetFilters = () => {
        setFilters(DEFAULT_FILTERS);
        closeModal();
    };


    return (
        <div className={styles.liningOver}>
            <motion.div
                className={styles.lining} 
                onClick={(e) => e.stopPropagation()}
                initial={{ scale: '97%', opacity: 0 }}
                animate={{ scale: '100%', opacity: 1 }}
                exit={{ scale: '97%', opacity: 0 }}
                transition={{ duration: 0.3 }}
            >
                <div className={styles.header}>
                    <h1>Фильтр по витрине заявок</h1>
                    <IconContainer Icon={XIcon} onClick={closeModal} size = '20' color = 'black' className = '' />
                </div>
                <div className={styles.main} style={{height: '70vh'}}>
                    {field=='numbers' &&
                    <>
                    <div className='labelInputWrapper'>
                        <div className='label'>
                            <a>Номер заявки</a>
                            <p 
                                style={{fontSize: '11px'}}
                                className={`aWithLink ${filters.numbers.length===0 && 'disabledTextButton'}`}
                                onClick={() => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        numbers: [],
                                    }));
                                }}
                            >
                                Сбросить
                            </p>
                        </div>
                        <div className='labelInputRight'>
                            <div className='inputWrapper'>
                                <input 
                                    type='text' 
                                    value={searchNumber}
                                    onChange={(e) => setSearchNumber(e.target.value)}
                                    placeholder='Номер заявки' 
                                    minLength='' 
                                    maxLength='100' 
                                    required 
                                />
                            </div>

                            {loading ? <Loading />
                            : warning ? <WarningPanel text={warning} />
                            :
                            <>
                            <p className='description'>Первые 400 записей</p>
                            {filteredNumbers.length!==0 &&
                            <div className={miniTable.table}>
                                <div
                                    className={`${miniTable.line} ${miniTable.lineWithHover}`}
                                    style={{ gridTemplateColumns: '99fr 1fr' }}
                                    onClick={handleSelectAllNumbers}
                                >
                                    <div>
                                        Выбрать все {searchNumber && 'из показанных'}
                                    </div>
                                    <div className={miniTable.actions}>
                                        <input 
                                            type='checkbox'
                                            checked={allSelectedNumbers}
                                            readOnly={true}
                                        />
                                    </div>
                                </div>
                            </div>}
                            <div className={miniTable.table}>
                                {filteredNumbers.length===0 ? <WarningPanel text='Ничего не найдено' inTable={true} />
                                : filteredNumbers.map((entry, index) => (
                                    <div
                                        className={`${miniTable.line} ${miniTable.lineWithHover}`}
                                        key={index}
                                        style={{ gridTemplateColumns: '99fr 1fr' }}
                                        onClick={() => {
                                            setFilters(prev => ({
                                                ...prev,
                                                numbers: prev.numbers.includes(entry.number)
                                                    ? prev.numbers.filter(n => n !== entry.number)
                                                    : [...prev.numbers, entry.number]
                                            }));
                                        }}
                                    >
                                        <div>
                                            {formatNull(entry.number)} ({entry.occurrences})
                                        </div>
                                        <div className={miniTable.actions}>
                                            <input 
                                                type='checkbox'
                                                checked={filters.numbers.includes(entry.number)}
                                                readOnly={true}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            </>}
                        </div>
                    </div>
                    </>}

                    {field=='debtors' &&
                    <>
                    <div className='labelInputWrapper'>
                        <div className='label'>
                            <a>Тип карточки</a>
                            <p 
                                style={{fontSize: '11px'}}
                                className={`aWithLink ${!filters.category && 'disabledTextButton'}`}
                                onClick={() => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        category: '',
                                    }));
                                }}
                            >
                                Сбросить
                            </p>
                        </div>
                        <div className='labelInputRight'>
                            <div className='radioSwitchWrapper'>
                                <label className={`radioSwitchLabel ${filters.category === 'Назначения' ? 'selected' : ''}`}>
                                    <input
                                        type='radio'
                                        name='typeSelector'
                                        value='Назначения'
                                        checked={filters.category === 'Назначения'}
                                        onChange={handleChange('category')}
                                    />
                                    <span>Назначения</span>
                                </label>
                                <label className={`radioSwitchLabel ${filters.category === 'Жалобы' ? 'selected' : ''}`}>
                                    <input
                                        type='radio'
                                        name='typeSelector'
                                        value='Жалобы'
                                        checked={filters.category === 'Жалобы'}
                                        onChange={handleChange('category')}
                                    />
                                    <span>Жалобы</span>
                                </label>
                                <label className={`radioSwitchLabel ${filters.category === 'Общие' ? 'selected' : ''}`}>
                                    <input
                                        type='radio'
                                        name='typeSelector'
                                        value='Общие'
                                        checked={filters.category === 'Общие'}
                                        onChange={handleChange('category')}
                                    />
                                    <span>Общие</span>
                                </label>
                            </div>
                        </div>
                    </div>
                    <span className='horizontalLine'></span>
                    <div className='labelInputWrapper'>
                        <div className='label'>
                            <a>Должник</a>
                            <p 
                                style={{fontSize: '11px'}}
                                className={`aWithLink ${filters.debtors.length===0 && 'disabledTextButton'}`}
                                onClick={() => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        debtors: [],
                                    }));
                                }}
                            >
                                Сбросить
                            </p>
                        </div>
                        <div className='labelInputRight'>
                            <div className='inputWrapper'>
                                <input 
                                    type='text' 
                                    value={searchDebtor}
                                    onChange={(e) => setSearchDebtor(e.target.value)}
                                    placeholder='ФИО или наименование' 
                                    minLength='' 
                                    maxLength='150' 
                                    required 
                                />
                            </div>

                            {loading ? <Loading />
                            : warning ? <WarningPanel text={warning} />
                            :
                            <>
                            <p className='description'>Первые 400 записей</p>
                            {filteredDebtors.length!==0 &&
                            <div className={miniTable.table}>
                                <div
                                    className={`${miniTable.line} ${miniTable.lineWithHover}`}
                                    style={{ gridTemplateColumns: '99fr 1fr' }}
                                    onClick={handleSelectAllDebtors}
                                >
                                    <div>
                                        Выбрать все {searchDebtor && 'из показанных'}
                                    </div>
                                    <div className={miniTable.actions}>
                                        <input 
                                            type='checkbox'
                                            checked={allSelectedDebtors}
                                            readOnly={true}
                                        />
                                    </div>
                                </div>
                            </div>}
                            <div className={miniTable.table}>
                                {filteredDebtors.length===0 ? <WarningPanel text='Ничего не найдено' inTable={true} />
                                : filteredDebtors.map((entry, index) => (
                                    <div
                                        className={`${miniTable.line} ${miniTable.lineWithHover}`}
                                        key={index}
                                        style={{ gridTemplateColumns: '99fr 1fr' }}
                                        onClick={() => {
                                            setFilters(prev => ({
                                                ...prev,
                                                debtors: prev.debtors.includes(entry.id)
                                                    ? prev.debtors.filter(n => n !== entry.id)
                                                    : [...prev.debtors, entry.id]
                                            }));
                                        }}
                                    >
                                        <div>
                                            {entry.name} ({entry.occurrences})
                                        </div>
                                        <div className={miniTable.actions}>
                                            <input 
                                                type='checkbox'
                                                checked={filters.debtors.includes(entry.id)}
                                                readOnly={true}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            </>}
                        </div>
                    </div>
                    </>}

                    {field=='date_mailing' &&
                    <>
                    {filters.mailing_status=='Письма не отправлены' && <WarningPanel text='Вабран фильтр статуса "Рассылка не совершена", поэтому выбор дат рассылки приведет к пустой таблице' type='warning' />}
                    <div className='labelInputWrapper'>
                        <div className='label'>
                            <a>Дата рассылки предложения</a>
                            <p 
                                style={{fontSize: '11px'}}
                                className={`aWithLink ${(!filters.date_mailing) && 'disabledTextButton'}`}
                                onClick={() => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        date_mailing_from: '',
                                        date_mailing_to: '',
                                    }));
                                }}
                            >
                                Сбросить
                            </p>
                        </div>
                        <div className='labelInputRight'>
                            <div className='inputTwoDateWrapper'>
                                <input type='date' 
                                    value={filters.date_mailing_from?.split('T')[0]}
                                    onChange={handleChange('date_mailing_from')} 
                                    placeholder='' 
                                    minLength='' 
                                    maxLength='' 
                                    required 
                                />
                                <p>—</p>
                                <input type='date' 
                                    value={filters.date_mailing_to?.split('T')[0]}
                                    onChange={handleChange('date_mailing_to')} 
                                    placeholder='' 
                                    minLength='' 
                                    maxLength='' 
                                    required 
                                />
                            </div>
                            <p className='description'>Быстрый выбор интервала по крайним 12 месяцам</p>
                            <div className={miniTable.table}>
                                {months.map((month) => (
                                    <div
                                        className={`${miniTable.line} ${miniTable.lineWithHover}`}
                                        key={month.key}
                                        style={{ gridTemplateColumns: '99fr 1fr' }}
                                        onClick={() => {
                                            setSelectedMonths(prev => {
                                                const newSelected = prev.includes(month.key)
                                                    ? prev.filter(k => k !== month.key)
                                                    : [...prev, month.key];

                                                if (newSelected.length === 0) {
                                                    setFilters(f => ({ ...f, date_mailing_from: '', date_mailing_to: '' }));
                                                } else {
                                                    const selectedRanges = months.filter(m => newSelected.includes(m.key));
                                                    const from = selectedRanges.reduce((min, m) => m.from < min ? m.from : min, selectedRanges[0].from);
                                                    const to = selectedRanges.reduce((max, m) => m.to > max ? m.to : max, selectedRanges[0].to);
                                                    setFilters(f => ({ ...f, date_mailing_from: from, date_mailing_to: to }));
                                                }

                                                return newSelected;
                                            });
                                        }}
                                    >
                                        <div>
                                            {month.label}
                                        </div>
                                        <div className={miniTable.actions}>
                                            <input 
                                                type='checkbox'
                                                checked={selectedMonths.includes(month.key)}
                                                readOnly={true}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <p className='description'>Выбор по датам (Первые 400 записей)</p>
                            <div className={miniTable.table}>
                                {entries.length===0 ? <WarningPanel text='Ничего не найдено' inTable={true} />
                                : entries.map((entry, index) => (
                                    <div
                                        className={`${miniTable.line} ${miniTable.lineWithHover}`}
                                        key={index}
                                        style={{ gridTemplateColumns: '99fr 1fr' }}
                                        onClick={() => {
                                            setDateMailing(prev =>
                                                prev.includes(entry.date_mailing)
                                                    ? prev.filter(d => d !== entry.date_mailing)
                                                    : [...prev, entry.date_mailing]
                                            );
                                        }}
                                    >
                                        <div>
                                            {formatDate(entry.date_mailing)} ({entry.occurrences})
                                        </div>
                                        <div className={miniTable.actions}>
                                            <input 
                                                type='checkbox'
                                                checked={dateMailing.includes(entry.date_mailing)}
                                                readOnly={true}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    </>}

                    {field=='date_consent_deadline' &&
                    <>
                    {filters.mailing_status=='Письма не отправлены' && <WarningPanel text='Вабран фильтр статуса "Рассылка не совершена", поэтому выбор крайних дат предоставления согласия приведет к пустой таблице' type='warning' />}
                    <div className='labelInputWrapper'>
                        <div className='label'>
                            <a>Крайний срок предоставления согласия</a>
                            <p 
                                style={{fontSize: '11px'}}
                                className={`aWithLink ${(!filters.date_consent_deadline_from && !filters.date_consent_deadline_to) && 'disabledTextButton'}`}
                                onClick={() => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        date_consent_deadline_from: '',
                                        date_consent_deadline_to: '',
                                    }));
                                }}
                            >
                                Сбросить
                            </p>
                        </div>
                        <div className='labelInputRight'>
                            <div className='inputTwoDateWrapper'>
                                <input type='date' 
                                    value={filters.date_consent_deadline_from?.split('T')[0]}
                                    onChange={handleChange('date_consent_deadline_from')} 
                                    placeholder='' 
                                    minLength='' 
                                    maxLength='' 
                                    required 
                                />
                                <p>—</p>
                                <input type='date' 
                                    value={filters.date_consent_deadline_to?.split('T')[0]}
                                    onChange={handleChange('date_consent_deadline_to')} 
                                    placeholder='' 
                                    minLength='' 
                                    maxLength='' 
                                    required 
                                />
                            </div>
                            <p className='description'>Быстрый выбор интервала по крайним 12 месяцам</p>
                            <div className={miniTable.table}>
                                {months.map((month) => (
                                    <div
                                        className={`${miniTable.line} ${miniTable.lineWithHover}`}
                                        key={month.key}
                                        style={{ gridTemplateColumns: '99fr 1fr' }}
                                        onClick={() => {
                                            setSelectedMonths(prev => {
                                                const newSelected = prev.includes(month.key)
                                                    ? prev.filter(k => k !== month.key)
                                                    : [...prev, month.key];

                                                if (newSelected.length === 0) {
                                                    setFilters(f => ({ ...f, date_consent_deadline_from: '', date_consent_deadline_to: '' }));
                                                } else {
                                                    const selectedRanges = months.filter(m => newSelected.includes(m.key));
                                                    const from = selectedRanges.reduce((min, m) => m.from < min ? m.from : min, selectedRanges[0].from);
                                                    const to = selectedRanges.reduce((max, m) => m.to > max ? m.to : max, selectedRanges[0].to);
                                                    setFilters(f => ({ ...f, date_consent_deadline_from: from, date_consent_deadline_to: to }));
                                                }

                                                return newSelected;
                                            });
                                        }}
                                    >
                                        <div>
                                            {month.label}
                                        </div>
                                        <div className={miniTable.actions}>
                                            <input 
                                                type='checkbox'
                                                checked={selectedMonths.includes(month.key)}
                                                readOnly={true}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <p className='description'>Выбор по датам (Первые 400 записей)</p>
                            <div className={miniTable.table}>
                                {entries.length===0 ? <WarningPanel text='Ничего не найдено' inTable={true} />
                                : entries.map((entry, index) => (
                                    <div
                                        className={`${miniTable.line} ${miniTable.lineWithHover}`}
                                        key={index}
                                        style={{ gridTemplateColumns: '99fr 1fr' }}
                                        onClick={() => {
                                            setDateMailing(prev =>
                                                prev.includes(entry.date_consent_deadline)
                                                    ? prev.filter(d => d !== entry.date_consent_deadline)
                                                    : [...prev, entry.date_consent_deadline]
                                            );
                                        }}
                                    >
                                        <div>
                                            {formatDate(entry.date_consent_deadline)} ({entry.occurrences})
                                        </div>
                                        <div className={miniTable.actions}>
                                            <input 
                                                type='checkbox'
                                                checked={dateMailing.includes(entry.date_consent_deadline)}
                                                readOnly={true}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    </>}

                    {field=='count_pretenders' &&
                    <>
                    <div className='labelInputWrapper'>
                        <div className='label'>
                            <a>Количество претендующих</a>
                            <p 
                                style={{fontSize: '11px'}}
                                className={`aWithLink ${filters.count_pretenders.length===0 && 'disabledTextButton'}`}
                                onClick={() => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        count_pretenders: [],
                                    }));
                                }}
                            >
                                Сбросить
                            </p>
                        </div>
                        <div className='labelInputRight'>
                            <div className='inputWrapper'>
                                <input 
                                    type='text' 
                                    value={searchCountPretenders}
                                    onChange={(e) => setSearchCountPretenders(e.target.value)}
                                    placeholder='Введите число' 
                                    minLength='' 
                                    maxLength='100' 
                                    required 
                                />
                            </div>

                            {loading ? <Loading />
                            : warning ? <WarningPanel text={warning} />
                            :
                            <>
                            {filteredNumbers.length!==0 &&
                            <div className={miniTable.table}>
                                <div
                                    className={`${miniTable.line} ${miniTable.lineWithHover}`}
                                    style={{ gridTemplateColumns: '99fr 1fr' }}
                                    onClick={handleSelectAllCounts}
                                >
                                    <div>
                                        Выбрать все {searchCountPretenders && 'из показанных'}
                                    </div>
                                    <div className={miniTable.actions}>
                                        <input 
                                            type='checkbox'
                                            checked={allSelectedCounts}
                                            readOnly={true}
                                        />
                                    </div>
                                </div>
                            </div>}
                            <div className={miniTable.table}>
                                {filteredCountPretenders.length===0 ? <WarningPanel text='Ничего не найдено' inTable={true} />
                                : filteredCountPretenders.map((entry, index) => (
                                    <div
                                        className={`${miniTable.line} ${miniTable.lineWithHover}`}
                                        key={index}
                                        style={{ gridTemplateColumns: '99fr 1fr' }}
                                        onClick={() => {
                                            setFilters(prev => ({
                                                ...prev,
                                                count_pretenders: prev.count_pretenders.includes(entry.applications_count)
                                                    ? prev.count_pretenders.filter(n => n !== entry.applications_count)
                                                    : [...prev.count_pretenders, entry.applications_count]
                                            }));
                                        }}
                                    >
                                        <div>
                                            {formatNull(entry.applications_count)} ({entry.cases_count})
                                        </div>
                                        <div className={miniTable.actions}>
                                            <input 
                                                type='checkbox'
                                                checked={filters.count_pretenders.includes(entry.applications_count)}
                                                readOnly={true}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            </>}
                        </div>
                    </div>
                    </>}

                    {field=='mailing_status' &&
                    <>
                    <div className='labelInputWrapper'>
                        <div className='label'>
                            <a>Статус рассылки</a>
                            <p 
                                style={{fontSize: '11px'}}
                                className={`aWithLink ${!filters.mailing_status && 'disabledTextButton'}`}
                                onClick={() => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        mailing_status: '',
                                    }));
                                }}
                            >
                                Сбросить
                            </p>
                        </div>
                        <div className='labelInputRight'>
                            <div className='labelInputRight'>
                                <div className='radioSwitchWrapper'>
                                    <label className={`radioSwitchLabel ${filters.mailing_status === 'Письма не отправлены' ? 'selected' : ''}`}>
                                        <input
                                            type='radio'
                                            name='typeSelector'
                                            value='Письма не отправлены'
                                            checked={filters.mailing_status === 'Письма не отправлены'}
                                            onChange={handleChange('mailing_status')}
                                        />
                                        <span>Рассылка не совершена</span>
                                    </label>
                                    <label className={`radioSwitchLabel ${filters.mailing_status === 'Письма отправлены' ? 'selected' : ''}`}>
                                        <input
                                            type='radio'
                                            name='typeSelector'
                                            value='Письма отправлены'
                                            checked={filters.mailing_status === 'Письма отправлены'}
                                            onChange={handleChange('mailing_status')}
                                        />
                                        <span>Рассылка совершена</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='labelInputWrapper'>
                        <div className='label'>
                            <a>Управляющий</a>
                            <p 
                                style={{fontSize: '11px'}}
                                className={`aWithLink ${!filters.user_status && 'disabledTextButton'}`}
                                onClick={() => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        user_status: '',
                                    }));
                                }}
                            >
                                Сбросить
                            </p>
                        </div>
                        <div className='labelInputRight'>
                            <div className='labelInputRight'>
                                <div className='radioSwitchWrapper'>
                                    <label className={`radioSwitchLabel ${filters.user_status === 'Выбор не произведен' ? 'selected' : ''}`}>
                                        <input
                                            type='radio'
                                            name='typeSelector'
                                            value='Выбор не произведен'
                                            checked={filters.user_status === 'Выбор не произведен'}
                                            onChange={handleChange('user_status')}
                                        />
                                        <span>Выбор не произведен</span>
                                    </label>
                                    <label className={`radioSwitchLabel ${filters.user_status === 'Кандидатура выбрана' ? 'selected' : ''}`}>
                                        <input
                                            type='radio'
                                            name='typeSelector'
                                            value='Кандидатура выбрана'
                                            checked={filters.user_status === 'Кандидатура выбрана'}
                                            onChange={handleChange('user_status')}
                                        />
                                        <span>Кандидатура выбрана СРО</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                    </>}
                </div>
                <div className={styles.bottom}>
                    {field!==null &&
                    <>
                    <div>Выберите параметры</div>
                    <div className='horizontalContainer'>
                        <button 
                            type='button' 
                            className='button optionalButton red' 
                            onClick={resetFilters}
                        >
                            Сбросить все фильтры
                        </button>
                        <button 
                            type='button' 
                            className='button' 
                            onClick={addFilter}
                        >
                            Применить фильтр
                        </button>
                    </div>
                    </>}
                </div>
            </motion.div>
        </div>
    )
}
